import { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

// ─── Country data ─────────────────────────────────────────────────────────────
const COUNTRIES = [
  { group: 'Benelux', options: [
    { value: 'LU', label: 'Luxembourg' }, { value: 'BE', label: 'Belgium' }, { value: 'NL', label: 'Netherlands' },
  ]},
  { group: 'Western Europe', options: [
    { value: 'FR', label: 'France' }, { value: 'DE', label: 'Germany' }, { value: 'AT', label: 'Austria' },
    { value: 'CH', label: 'Switzerland' }, { value: 'GB', label: 'United Kingdom' }, { value: 'IE', label: 'Ireland' },
    { value: 'IT', label: 'Italy' }, { value: 'ES', label: 'Spain' }, { value: 'PT', label: 'Portugal' },
    { value: 'LI', label: 'Liechtenstein' }, { value: 'MC', label: 'Monaco' },
  ]},
  { group: 'Nordic', options: [
    { value: 'SE', label: 'Sweden' }, { value: 'DK', label: 'Denmark' }, { value: 'FI', label: 'Finland' },
    { value: 'NO', label: 'Norway' }, { value: 'IS', label: 'Iceland' },
  ]},
  { group: 'Eastern Europe', options: [
    { value: 'PL', label: 'Poland' }, { value: 'CZ', label: 'Czech Republic' }, { value: 'SK', label: 'Slovakia' },
    { value: 'HU', label: 'Hungary' }, { value: 'RO', label: 'Romania' }, { value: 'BG', label: 'Bulgaria' },
    { value: 'HR', label: 'Croatia' }, { value: 'SI', label: 'Slovenia' }, { value: 'EE', label: 'Estonia' },
    { value: 'LV', label: 'Latvia' }, { value: 'LT', label: 'Lithuania' }, { value: 'GR', label: 'Greece' },
    { value: 'CY', label: 'Cyprus' }, { value: 'MT', label: 'Malta' }, { value: 'RS', label: 'Serbia' },
    { value: 'UA', label: 'Ukraine' }, { value: 'TR', label: 'Turkey' },
  ]},
  { group: 'Americas', options: [
    { value: 'US', label: 'United States' }, { value: 'CA', label: 'Canada' }, { value: 'BR', label: 'Brazil' },
    { value: 'MX', label: 'Mexico' }, { value: 'AR', label: 'Argentina' }, { value: 'CL', label: 'Chile' },
  ]},
  { group: 'Asia & Pacific', options: [
    { value: 'JP', label: 'Japan' }, { value: 'CN', label: 'China' }, { value: 'IN', label: 'India' },
    { value: 'SG', label: 'Singapore' }, { value: 'HK', label: 'Hong Kong' }, { value: 'KR', label: 'South Korea' },
    { value: 'AU', label: 'Australia' }, { value: 'NZ', label: 'New Zealand' }, { value: 'TH', label: 'Thailand' },
    { value: 'MY', label: 'Malaysia' }, { value: 'ID', label: 'Indonesia' },
  ]},
  { group: 'Middle East & Africa', options: [
    { value: 'AE', label: 'United Arab Emirates' }, { value: 'SA', label: 'Saudi Arabia' },
    { value: 'QA', label: 'Qatar' }, { value: 'IL', label: 'Israel' },
    { value: 'ZA', label: 'South Africa' }, { value: 'NG', label: 'Nigeria' }, { value: 'EG', label: 'Egypt' },
  ]},
  { group: 'Other', options: [
    { value: 'RU', label: 'Russia' }, { value: 'OTHER', label: 'Other' },
  ]},
];

const ALL_OPTIONS = COUNTRIES.flatMap(g => g.options);

// ─── Shared styles ─────────────────────────────────────────────────────────────
const C = {
  navy: '#1e3a5f', navyLt: '#1e4d8c',
  bg: '#f4f6f9', white: 'white',
  border: '#e5e7eb', text: '#111827',
  textMd: '#374151', textSm: '#6b7280', textXs: '#9ca3af',
  green: '#16a34a', greenBg: '#dcfce7',
  red: '#dc2626',
};

const btnStyle = (bg, color, borderColor) => ({
  background: bg, color,
  border: borderColor ? `1px solid ${borderColor}` : 'none',
  borderRadius: 8, padding: '10px 24px',
  fontWeight: 700, fontSize: 13,
  cursor: 'pointer', transition: 'opacity 0.2s',
});

// ─── Steps config ──────────────────────────────────────────────────────────────
const STEPS = [
  { number: 1, label: 'Personal Details',    icon: '👤' },
  { number: 2, label: 'KYC Verification',    icon: '🔍' },
  { number: 3, label: 'Suitability Chat',    icon: '🤖' },
  { number: 4, label: 'Review & Submit',     icon: '✍️'  },
];

// ─── NationalityDropdown ───────────────────────────────────────────────────────
const NationalityDropdown = ({ label, value, onChange, required }) => {
  const [open,   setOpen]   = useState(false);
  const [search, setSearch] = useState('');
  const selected = ALL_OPTIONS.find(o => o.value === value);
  const filtered = search.trim()
    ? COUNTRIES.map(g => ({ ...g, options: g.options.filter(o => o.label.toLowerCase().includes(search.toLowerCase())) })).filter(g => g.options.length > 0)
    : COUNTRIES;

  return (
    <div style={{ marginBottom: 18, position: 'relative' }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.textMd, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {label}{required && <span style={{ color: C.red }}> *</span>}
      </label>
      <button type="button" onClick={() => setOpen(v => !v)} style={{
        width: '100%', padding: '10px 14px', borderRadius: 8,
        border: `1px solid ${open ? C.navy : C.border}`,
        fontSize: 14, color: selected ? C.text : C.textXs,
        background: C.white, outline: 'none', cursor: 'pointer',
        textAlign: 'left', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        boxSizing: 'border-box',
      }}>
        <span>{selected ? selected.label : 'Select…'}</span>
        <span style={{ fontSize: 10, color: C.textXs }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 499 }} onClick={() => { setOpen(false); setSearch(''); }} />
          <div style={{
            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 500,
            background: C.white, borderRadius: 8, marginTop: 4,
            border: `1px solid ${C.border}`, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', overflow: 'hidden',
          }}>
            <div style={{ padding: '8px 10px', borderBottom: `1px solid #f0f0f0`, background: '#fafafa' }}>
              <input autoFocus type="text" placeholder="Search country…" value={search}
                onChange={e => setSearch(e.target.value)}
                style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 12, outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ maxHeight: 200, overflowY: 'auto' }}>
              {filtered.map(group => (
                <div key={group.group}>
                  <div style={{ padding: '5px 12px 3px', fontSize: 9, fontWeight: 700, color: C.textXs, textTransform: 'uppercase', letterSpacing: '0.08em', background: '#f8fafc', borderBottom: `1px solid #f0f0f0` }}>
                    {group.group}
                  </div>
                  {group.options.map(opt => (
                    <div key={opt.value} onClick={() => { onChange(opt.value); setOpen(false); setSearch(''); }}
                      style={{ padding: '8px 14px', fontSize: 13, cursor: 'pointer',
                        color: opt.value === value ? C.navy : C.text,
                        fontWeight: opt.value === value ? 700 : 400,
                        background: opt.value === value ? '#eff6ff' : C.white,
                      }}>
                      {opt.label}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ─── Field ─────────────────────────────────────────────────────────────────────
const Field = ({ label, type = 'text', value, onChange, options, required, disabled }) => (
  <div style={{ marginBottom: 18 }}>
    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.textMd, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
      {label}{required && <span style={{ color: C.red }}> *</span>}
    </label>
    {options ? (
      <select value={value} onChange={e => !disabled && onChange(e.target.value)} disabled={disabled} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14, color: disabled ? C.textSm : C.text, background: disabled ? '#f3f4f6' : C.white, outline: 'none', cursor: disabled ? 'not-allowed' : 'auto' }}>
        <option value="">Select…</option>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    ) : (
      <input type={type} value={value} onChange={e => onChange(e.target.value)} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, background: C.white, outline: 'none', boxSizing: 'border-box' }} />
    )}
  </div>
);

// ─── ProgressBar ───────────────────────────────────────────────────────────────
const ProgressBar = ({ current }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 36 }}>
    {STEPS.map((step, i) => {
      const isDone   = current > step.number;
      const isActive = current === step.number;
      return (
        <div key={step.number} style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <div style={{ textAlign: 'center', flex: 1 }}>
            <div style={{
              width: 36, height: 36, borderRadius: '50%',
              margin: '0 auto 8px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: isDone ? C.green : isActive ? C.navy : '#e5e7eb',
              color: isDone || isActive ? C.white : C.textXs,
              fontWeight: 700, fontSize: 13,
              boxShadow: isActive ? '0 0 0 4px rgba(30,58,95,0.15)' : 'none',
              transition: 'all 0.3s',
            }}>
              {isDone ? '✓' : step.icon}
            </div>
            <p style={{ fontSize: 10, margin: 0, fontWeight: isActive ? 700 : 400, color: isDone ? C.green : isActive ? C.navy : C.textXs }}>
              {step.label}
            </p>
          </div>
          {i < STEPS.length - 1 && (
            <div style={{ height: 2, flex: 0.5, marginBottom: 22, background: isDone ? C.green : '#e5e7eb', transition: 'background 0.3s' }} />
          )}
        </div>
      );
    })}
  </div>
);

// ─── Step 1 — Personal Details ─────────────────────────────────────────────────
const Step1 = ({ data, setData, onNext, investorId, token }) => {
  const handleSubmit = async () => {
    if (!data.full_name || !data.email || !data.nationality || !data.address_country || !data.investor_type) {
      alert('Please fill in all required fields.');
      return;
    }
    // Save personal data immediately so suitability AI has access to age/nationality
    try {
      await axios.post(`${API_URL}/investors/${investorId}/save-personal`, {
        token,
        personal: data,
      });
    } catch (err) {
      console.error('Failed to save personal data early:', err);
    }
    onNext();
  };

  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: C.navy, margin: '0 0 6px' }}>Personal Details</h2>
      <p style={{ fontSize: 13, color: C.textSm, margin: '0 0 28px' }}>
        Please provide your personal information to begin the onboarding process.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 20px' }}>
        <Field label="Full Name"    value={data.full_name}    onChange={v => setData(d => ({ ...d, full_name: v }))}    required />
        <Field label="Email"        type="email" value={data.email} onChange={v => setData(d => ({ ...d, email: v }))} required />
        <NationalityDropdown label="Nationality"          value={data.nationality}     onChange={v => setData(d => ({ ...d, nationality: v }))}     required />
        <NationalityDropdown label="Country of Residence" value={data.address_country} onChange={v => setData(d => ({ ...d, address_country: v }))} required />
        <Field label="Date of Birth" type="date" value={data.date_of_birth} onChange={v => setData(d => ({ ...d, date_of_birth: v }))} />
        <Field label="Phone Number"  type="tel"  value={data.phone}         onChange={v => setData(d => ({ ...d, phone: v }))} />
      </div>
      <Field label="Investor Type" value={data.investor_type} onChange={v => setData(d => ({ ...d, investor_type: v }))}
        options={[
          { value: 'RETAIL',                label: 'Retail Investor' },
          { value: 'PROFESSIONAL',          label: 'Professional Investor' },
          { value: 'ELECTIVE_PROFESSIONAL', label: 'Elective Professional' },
          { value: 'WELL_INFORMED',         label: 'Well-Informed Investor (AIF/RAIF/SIF)' },
          { value: 'ELIGIBLE_COUNTERPARTY', label: 'Eligible Counterparty' },
        ]}
        required
        disabled
      />
      <Field label="Preferred Language" value={data.preferred_language} onChange={v => setData(d => ({ ...d, preferred_language: v }))}
        options={[
          { value: 'EN', label: 'English' },
          { value: 'FR', label: 'French' },
          { value: 'DE', label: 'German' },
        ]}
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
        <button onClick={handleSubmit} style={btnStyle(C.navy, C.white)}>Continue →</button>
      </div>
    </div>
  );
};

// ─── Translations for Steps 2 & 4 ─────────────────────────────────────────────
const T = {
  EN: {
    chat: {
      loading: 'Starting your suitability assessment…',
      header: 'Suitability Assessment',
      sub: 'Our AI advisor will ask you a few questions to assess whether this investment is suitable for you under MiFID II Article 25.',
      thinking: 'Thinking…',
      placeholder: 'Type your response… (Enter to send)',
      send: 'Send',
      doneBanner: '✓ Assessment complete',
      doneSubtext: 'Your responses have been recorded. Click Continue to proceed.',
      back: '← Back', next: 'Continue →',
      errorTitle: (underage) => underage ? 'Application cannot proceed' : 'Something went wrong',
      errorLabel: (underage) => underage ? 'Age requirement not met' : 'Assessment error',
      errorMsg: 'You must be at least 18 years old to invest in this fund. This is a legal requirement under Luxembourg financial regulations.',
      errorNext: 'What happens next?',
      errorNextBody: 'Your application has been automatically rejected and your fund manager has been notified.',
      errorNextFooter: 'If you believe your date of birth was entered incorrectly, please contact your fund manager directly to resolve this.',
      errorGeneric: 'Sorry, something went wrong. Please try again.',
    },
    kyc: {
      title: 'KYC Identity Verification',
      sub: 'We are required by law to verify your identity before you can invest.',
      sumsub: 'Sumsub Identity Verification',
      sumsubDesc: 'You will be asked to take a photo of your ID document and a selfie. This process takes approximately 2 minutes.',
      soon: '⚠️ KYC integration coming soon — click Continue to proceed',
      back: '← Back', next: 'Continue →',
    },
    review: {
      title: 'Review & Submit',
      sub: 'Please review your application details and confirm your investment amount.',
      personalSection: 'Personal Details',
      labels: { name: 'Full Name', email: 'Email', nationality: 'Nationality', investorType: 'Investor Type' },
      suitabilitySection: 'Suitability Assessment',
      suitabilityDone: '✓ Completed — under review by your compliance officer',
      amountLabel: 'Investment Amount (EUR)',
      amountMin: 'Minimum investment is €1,000',
      checkboxText: (amt) => <>I confirm that I have read all relevant documents, understand the risks involved, and wish to invest <strong>{amt}</strong> in this fund.</>,
      back: '← Back', submit: '✅ Submit Application', submitting: '⏳ Submitting…',
    },
    success: {
      title: 'Application Submitted!',
      body: (name) => <>Thank you, <strong>{name || 'Investor'}</strong>. Your application has been received and is now under review by the compliance team.</>,
      footer: 'You will be contacted once your application has been reviewed. If you have any questions, please contact your fund manager.',
    },
  },
  FR: {
    chat: {
      loading: 'Démarrage de votre évaluation d\'adéquation…',
      header: 'Évaluation d\'adéquation',
      sub: 'Notre conseiller IA vous posera quelques questions pour évaluer si cet investissement vous convient, conformément à l\'article 25 de MiFID II.',
      thinking: 'Réflexion en cours…',
      placeholder: 'Tapez votre réponse… (Entrée pour envoyer)',
      send: 'Envoyer',
      doneBanner: '✓ Évaluation terminée',
      doneSubtext: 'Vos réponses ont été enregistrées. Cliquez sur Continuer pour poursuivre.',
      back: '← Retour', next: 'Continuer →',
      errorTitle: (underage) => underage ? 'La demande ne peut pas être traitée' : 'Une erreur s\'est produite',
      errorLabel: (underage) => underage ? 'Condition d\'âge non remplie' : 'Erreur d\'évaluation',
      errorMsg: 'Vous devez avoir au moins 18 ans pour investir dans ce fonds. Il s\'agit d\'une exigence légale en vertu de la réglementation financière luxembourgeoise.',
      errorNext: 'Que se passe-t-il ensuite ?',
      errorNextBody: 'Votre demande a été automatiquement rejetée et votre gestionnaire de fonds en a été informé.',
      errorNextFooter: 'Si vous pensez que votre date de naissance a été saisie incorrectement, veuillez contacter directement votre gestionnaire de fonds.',
      errorGeneric: 'Désolé, une erreur s\'est produite. Veuillez réessayer.',
    },
    kyc: {
      title: 'Vérification d\'identité KYC',
      sub: 'La loi nous oblige à vérifier votre identité avant tout investissement.',
      sumsub: 'Vérification d\'identité Sumsub',
      sumsubDesc: 'Il vous sera demandé de photographier votre pièce d\'identité et de prendre un selfie. Ce processus prend environ 2 minutes.',
      soon: '⚠️ Intégration KYC bientôt disponible — cliquez sur Continuer pour poursuivre',
      back: '← Retour', next: 'Continuer →',
    },
    review: {
      title: 'Vérification et soumission',
      sub: 'Veuillez vérifier les détails de votre dossier et confirmer votre montant d\'investissement.',
      personalSection: 'Informations personnelles',
      labels: { name: 'Nom complet', email: 'E-mail', nationality: 'Nationalité', investorType: 'Type d\'investisseur' },
      suitabilitySection: 'Évaluation d\'adéquation',
      suitabilityDone: '✓ Terminée — en cours de révision par votre responsable de conformité',
      amountLabel: 'Montant d\'investissement (EUR)',
      amountMin: 'L\'investissement minimum est de 1 000 €',
      checkboxText: (amt) => <>Je confirme avoir lu tous les documents pertinents, compris les risques et souhaite investir <strong>{amt}</strong> dans ce fonds.</>,
      back: '← Retour', submit: '✅ Soumettre la demande', submitting: '⏳ Envoi en cours…',
    },
    success: {
      title: 'Demande soumise !',
      body: (name) => <>Merci, <strong>{name || 'Investisseur'}</strong>. Votre demande a bien été reçue et est en cours d\'examen par l\'équipe de conformité.</>,
      footer: 'Vous serez contacté dès que votre dossier aura été examiné. Pour toute question, veuillez contacter votre gestionnaire de fonds.',
    },
  },
  DE: {
    chat: {
      loading: 'Ihre Eignungsbeurteilung wird gestartet…',
      header: 'Eignungsbeurteilung',
      sub: 'Unser KI-Berater stellt Ihnen einige Fragen, um gemäß MiFID II Artikel 25 zu beurteilen, ob diese Investition für Sie geeignet ist.',
      thinking: 'Wird verarbeitet…',
      placeholder: 'Antwort eingeben… (Enter zum Senden)',
      send: 'Senden',
      doneBanner: '✓ Beurteilung abgeschlossen',
      doneSubtext: 'Ihre Antworten wurden gespeichert. Klicken Sie auf Weiter, um fortzufahren.',
      back: '← Zurück', next: 'Weiter →',
      errorTitle: (underage) => underage ? 'Antrag kann nicht bearbeitet werden' : 'Ein Fehler ist aufgetreten',
      errorLabel: (underage) => underage ? 'Altersanforderung nicht erfüllt' : 'Beurteilungsfehler',
      errorMsg: 'Sie müssen mindestens 18 Jahre alt sein, um in diesen Fonds zu investieren. Dies ist eine gesetzliche Anforderung gemäß luxemburgischem Finanzrecht.',
      errorNext: 'Was passiert als nächstes?',
      errorNextBody: 'Ihr Antrag wurde automatisch abgelehnt und Ihr Fondsmanager wurde benachrichtigt.',
      errorNextFooter: 'Wenn Sie glauben, dass Ihr Geburtsdatum falsch eingegeben wurde, wenden Sie sich bitte direkt an Ihren Fondsmanager.',
      errorGeneric: 'Entschuldigung, etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.',
    },
    kyc: {
      title: 'KYC-Identitätsprüfung',
      sub: 'Wir sind gesetzlich verpflichtet, Ihre Identität vor einer Investition zu überprüfen.',
      sumsub: 'Sumsub Identitätsprüfung',
      sumsubDesc: 'Sie werden gebeten, ein Foto Ihres Ausweisdokuments und ein Selfie aufzunehmen. Dieser Vorgang dauert ca. 2 Minuten.',
      soon: '⚠️ KYC-Integration demnächst verfügbar — klicken Sie auf Weiter, um fortzufahren',
      back: '← Zurück', next: 'Weiter →',
    },
    review: {
      title: 'Überprüfen & Einreichen',
      sub: 'Bitte überprüfen Sie Ihre Angaben und bestätigen Sie Ihren Anlagebetrag.',
      personalSection: 'Persönliche Daten',
      labels: { name: 'Vollständiger Name', email: 'E-Mail', nationality: 'Staatsangehörigkeit', investorType: 'Anlegertyp' },
      suitabilitySection: 'Eignungsbeurteilung',
      suitabilityDone: '✓ Abgeschlossen — wird von Ihrem Compliance-Beauftragten geprüft',
      amountLabel: 'Anlagebetrag (EUR)',
      amountMin: 'Mindestanlage beträgt 1.000 €',
      checkboxText: (amt) => <>Ich bestätige, alle relevanten Dokumente gelesen zu haben, die Risiken zu verstehen und <strong>{amt}</strong> in diesen Fonds investieren zu wollen.</>,
      back: '← Zurück', submit: '✅ Antrag einreichen', submitting: '⏳ Wird eingereicht…',
    },
    success: {
      title: 'Antrag eingereicht!',
      body: (name) => <>Vielen Dank, <strong>{name || 'Anleger'}</strong>. Ihr Antrag ist eingegangen und wird vom Compliance-Team geprüft.</>,
      footer: 'Sie werden kontaktiert, sobald Ihr Antrag bearbeitet wurde. Bei Fragen wenden Sie sich bitte an Ihren Fondsmanager.',
    },
  },
};

// ─── Step 2 — AML & KYC Declaration ───────────────────────────────────────────
const SOURCE_OF_WEALTH_OPTIONS = [
  'Employment income', 'Self-employment / Business ownership', 'Investment returns',
  'Inheritance', 'Property sale', 'Pension / Retirement funds',
  'Gift', 'Legal settlement', 'Other',
];
const SOURCE_OF_FUNDS_OPTIONS = [
  'Personal savings', 'Salary / Bonus', 'Business income',
  'Investment proceeds', 'Inheritance / Gift', 'Property sale proceeds',
  'Pension / Retirement withdrawal', 'Loan / Credit facility', 'Other',
];

const Step2 = ({ onNext, onBack, lang = 'EN', amlData, setAmlData }) => {
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!amlData.source_of_wealth) e.source_of_wealth = 'Required';
    if (!amlData.source_of_funds)  e.source_of_funds  = 'Required';
    if (amlData.source_of_wealth === 'Other' && !amlData.source_of_wealth_details?.trim()) e.source_of_wealth_details = 'Please provide details';
    if (amlData.source_of_funds  === 'Other' && !amlData.source_of_funds_details?.trim())  e.source_of_funds_details  = 'Please provide details';
    if (!amlData.sanctions_declaration) e.sanctions_declaration = 'You must confirm this declaration';
    if (amlData.is_pep && !amlData.pep_details?.trim()) e.pep_details = 'Please provide details of your PEP status';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => { if (validate()) onNext(); };

  const inputStyle = (err) => ({
    width: '100%', padding: '10px 12px', borderRadius: 8,
    border: `1px solid ${err ? '#dc2626' : C.border}`,
    fontSize: 13, boxSizing: 'border-box', background: C.white,
    outline: 'none', marginTop: 6,
  });

  const labelStyle = {
    fontSize: 12, fontWeight: 600, color: C.textMd,
    textTransform: 'uppercase', letterSpacing: '0.04em',
  };

  const errStyle = { fontSize: 11, color: '#dc2626', marginTop: 4 };

  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: C.navy, margin: '0 0 6px' }}>
        AML & KYC Declaration
      </h2>
      <p style={{ fontSize: 13, color: C.textSm, margin: '0 0 24px' }}>
        As required by Luxembourg AML law and CSSF regulations, we must collect information about the origin of your funds before proceeding.
      </p>

      {/* Source of Wealth */}
      <div style={{ marginBottom: 18 }}>
        <label style={labelStyle}>Source of Wealth <span style={{ color: '#dc2626' }}>*</span></label>
        <p style={{ fontSize: 11, color: C.textXs, margin: '4px 0 0' }}>How did you accumulate your overall wealth?</p>
        <select value={amlData.source_of_wealth || ''} onChange={e => setAmlData(d => ({ ...d, source_of_wealth: e.target.value }))}
          style={inputStyle(errors.source_of_wealth)}>
          <option value="">Select…</option>
          {SOURCE_OF_WEALTH_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
        {errors.source_of_wealth && <p style={errStyle}>{errors.source_of_wealth}</p>}
        {amlData.source_of_wealth === 'Other' && (
          <textarea placeholder="Please describe your source of wealth…" value={amlData.source_of_wealth_details || ''}
            onChange={e => setAmlData(d => ({ ...d, source_of_wealth_details: e.target.value }))}
            style={{ ...inputStyle(errors.source_of_wealth_details), minHeight: 80, resize: 'vertical' }} />
        )}
        {amlData.source_of_wealth && amlData.source_of_wealth !== 'Other' && (
          <textarea placeholder="Optional: provide additional details…" value={amlData.source_of_wealth_details || ''}
            onChange={e => setAmlData(d => ({ ...d, source_of_wealth_details: e.target.value }))}
            style={{ ...inputStyle(false), minHeight: 60, resize: 'vertical', marginTop: 8 }} />
        )}
      </div>

      {/* Source of Funds */}
      <div style={{ marginBottom: 18 }}>
        <label style={labelStyle}>Source of Funds <span style={{ color: '#dc2626' }}>*</span></label>
        <p style={{ fontSize: 11, color: C.textXs, margin: '4px 0 0' }}>Where is the money for this specific investment coming from?</p>
        <select value={amlData.source_of_funds || ''} onChange={e => setAmlData(d => ({ ...d, source_of_funds: e.target.value }))}
          style={inputStyle(errors.source_of_funds)}>
          <option value="">Select…</option>
          {SOURCE_OF_FUNDS_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
        {errors.source_of_funds && <p style={errStyle}>{errors.source_of_funds}</p>}
        {amlData.source_of_funds === 'Other' && (
          <textarea placeholder="Please describe the source of funds…" value={amlData.source_of_funds_details || ''}
            onChange={e => setAmlData(d => ({ ...d, source_of_funds_details: e.target.value }))}
            style={{ ...inputStyle(errors.source_of_funds_details), minHeight: 80, resize: 'vertical' }} />
        )}
        {amlData.source_of_funds && amlData.source_of_funds !== 'Other' && (
          <textarea placeholder="Optional: provide additional details…" value={amlData.source_of_funds_details || ''}
            onChange={e => setAmlData(d => ({ ...d, source_of_funds_details: e.target.value }))}
            style={{ ...inputStyle(false), minHeight: 60, resize: 'vertical', marginTop: 8 }} />
        )}
      </div>

      {/* PEP Declaration */}
      <div style={{ marginBottom: 18, background: '#f8fafc', borderRadius: 10, padding: '16px', border: `1px solid ${C.border}` }}>
        <label style={labelStyle}>Politically Exposed Person (PEP) Declaration <span style={{ color: '#dc2626' }}>*</span></label>
        <p style={{ fontSize: 11, color: C.textXs, margin: '4px 0 12px' }}>
          A PEP is a person who holds or has held a prominent public function, or is a family member or close associate of such a person.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[
            { value: false, label: 'I am NOT a Politically Exposed Person' },
            { value: true,  label: 'I AM a Politically Exposed Person or close associate of one' },
          ].map(opt => (
            <label key={String(opt.value)} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
              <input type="radio" name="pep" checked={amlData.is_pep === opt.value}
                onChange={() => setAmlData(d => ({ ...d, is_pep: opt.value, pep_details: opt.value ? d.pep_details : '' }))}
                style={{ marginTop: 2, accentColor: C.navy }} />
              <span style={{ fontSize: 13, color: C.text }}>{opt.label}</span>
            </label>
          ))}
        </div>
        {amlData.is_pep && (
          <div style={{ marginTop: 12 }}>
            <textarea placeholder="Please describe your PEP status (role, country, relationship)…"
              value={amlData.pep_details || ''}
              onChange={e => setAmlData(d => ({ ...d, pep_details: e.target.value }))}
              style={{ ...inputStyle(errors.pep_details), minHeight: 80, resize: 'vertical' }} />
            {errors.pep_details && <p style={errStyle}>{errors.pep_details}</p>}
            <div style={{ marginTop: 10, background: '#fef3c7', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#92400e', fontWeight: 600 }}>
              ⚠️ Your application will require enhanced due diligence review by our compliance team before proceeding.
            </div>
          </div>
        )}
      </div>

      {/* Sanctions Declaration */}
      <div style={{ marginBottom: 24, background: '#f8fafc', borderRadius: 10, padding: '16px', border: `1px solid ${errors.sanctions_declaration ? '#dc2626' : C.border}` }}>
        <label style={labelStyle}>Sanctions Declaration <span style={{ color: '#dc2626' }}>*</span></label>
        <p style={{ fontSize: 11, color: C.textXs, margin: '4px 0 12px' }}>
          I confirm that I am not subject to any EU, UN, OFAC, or other international sanctions, and that I am not acting on behalf of any sanctioned individual or entity.
        </p>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
          <input type="checkbox" checked={amlData.sanctions_declaration || false}
            onChange={e => setAmlData(d => ({ ...d, sanctions_declaration: e.target.checked }))}
            style={{ marginTop: 2, accentColor: C.navy }} />
          <span style={{ fontSize: 13, color: C.text, fontWeight: 500 }}>
            I confirm I am not subject to any international sanctions and am not acting on behalf of a sanctioned party.
          </span>
        </label>
        {errors.sanctions_declaration && <p style={errStyle}>{errors.sanctions_declaration}</p>}
      </div>

      {/* Sumsub placeholder */}
      <div style={{ background: '#f0f7ff', borderRadius: 10, padding: '14px 16px', marginBottom: 24, border: '1px dashed #93c5fd', display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ fontSize: 24 }}>🪪</span>
        <div>
          <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: C.navy }}>Identity Verification — Coming Soon</p>
          <p style={{ margin: '2px 0 0', fontSize: 12, color: C.textSm }}>Full KYC with document verification and liveness check via Sumsub will be available shortly.</p>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <button onClick={onBack} style={btnStyle(C.white, C.navy, C.navy)}>← Back</button>
        <button onClick={handleNext} style={btnStyle(C.navy, C.white)}>Continue →</button>
      </div>
    </div>
  );
};

// ─── Warning translations (Step 3 gate) ───────────────────────────────────────
const WARN = {
  EN: {
    title:   'Suitability Assessment',
    sub:     'Before we begin, please read the following important information.',
    heading: '⚠️ Before you begin — please read carefully',
    bullets: [
      '<strong>Answer every question in detail.</strong> Vague or incomplete answers may result in an incomplete assessment and delay your application.',
      'You will be asked about your <strong>financial knowledge, investment experience, risk tolerance and objectives</strong>. Take your time with each question.',
      '<strong>Your sensitive financial information — such as income, assets or net worth — is never linked to your personal identity.</strong> It is used solely to assess suitability and is stored under a pseudonymous reference.',
      'Your responses are analysed by AI under <strong>MiFID II Article 25</strong> and reviewed by your compliance officer. Honest answers protect both you and the fund.',
    ],
    privacy: '🔒 Your financial responses are stored pseudonymously under a compliance reference — they cannot be traced back to your name or personal details.',
    back:    '← Back',
    begin:   'I understand — Begin Assessment →',
  },
  FR: {
    title:   'Évaluation de l\'adéquation',
    sub:     'Avant de commencer, veuillez lire attentivement les informations suivantes.',
    heading: '⚠️ Avant de commencer — veuillez lire attentivement',
    bullets: [
      '<strong>Répondez à chaque question en détail.</strong> Des réponses vagues ou incomplètes peuvent entraîner une évaluation incomplète et retarder votre dossier.',
      'Il vous sera demandé des informations sur vos <strong>connaissances financières, votre expérience en investissement, votre tolérance au risque et vos objectifs</strong>. Prenez le temps nécessaire.',
      '<strong>Vos informations financières sensibles — telles que vos revenus, actifs ou patrimoine net — ne sont jamais associées à votre identité personnelle.</strong> Elles sont utilisées uniquement pour évaluer l\'adéquation et stockées sous une référence pseudonyme.',
      'Vos réponses sont analysées par IA conformément à <strong>l\'article 25 de MiFID II</strong> et examinées par votre responsable de conformité. Des réponses honnêtes vous protègent, ainsi que le fonds.',
    ],
    privacy: '🔒 Vos réponses financières sont stockées de manière pseudonyme sous une référence de conformité — elles ne peuvent pas être reliées à votre nom ou à vos données personnelles.',
    back:    '← Retour',
    begin:   'Je comprends — Commencer l\'évaluation →',
  },
  DE: {
    title:   'Eignungsbeurteilung',
    sub:     'Bitte lesen Sie die folgenden wichtigen Informationen, bevor wir beginnen.',
    heading: '⚠️ Vor dem Start — bitte sorgfältig lesen',
    bullets: [
      '<strong>Beantworten Sie jede Frage ausführlich.</strong> Vage oder unvollständige Antworten können zu einer unvollständigen Beurteilung führen und Ihren Antrag verzögern.',
      'Sie werden nach Ihren <strong>Finanzkenntnissen, Anlageerfahrungen, Risikotoleranz und Anlagezielen</strong> gefragt. Nehmen Sie sich für jede Frage ausreichend Zeit.',
      '<strong>Ihre sensiblen Finanzdaten — wie Einkommen, Vermögen oder Nettovermögen — werden niemals mit Ihrer persönlichen Identität verknüpft.</strong> Sie dienen ausschließlich der Eignungsbeurteilung und werden unter einer pseudonymen Referenz gespeichert.',
      'Ihre Antworten werden von einer KI gemäß <strong>MiFID II Artikel 25</strong> analysiert und von Ihrem Compliance-Beauftragten geprüft. Ehrliche Antworten schützen Sie und den Fonds.',
    ],
    privacy: '🔒 Ihre finanziellen Antworten werden pseudonym unter einer Compliance-Referenz gespeichert — sie können nicht mit Ihrem Namen oder Ihren persönlichen Daten verknüpft werden.',
    back:    '← Zurück',
    begin:   'Ich verstehe — Beurteilung starten →',
  },
};

// ─── Step 3 — AI Suitability Chat ──────────────────────────────────────────────
const Step3 = ({ investorId, token, onNext, onBack, onSessionComplete, lang = 'EN' }) => {
  const t    = WARN[lang] || WARN.EN;
  const chat = (T[lang] || T.EN).chat;
  const [messages,     setMessages]     = useState([]);
  const [input,        setInput]        = useState('');
  const [sessionToken, setSessionToken] = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [sending,      setSending]      = useState(false);
  const [completed,    setCompleted]    = useState(false);
  const [error,        setError]        = useState(null);
  const [warned,       setWarned]       = useState(false);
  const bottomRef = useRef(null);

  // Start session on mount — only after warning acknowledged
  useEffect(() => {
    if (!warned) return;
    const startSession = async () => {
      try {
        const res = await axios.post(`${API_URL}/suitability/public/start`, {
          investor_id: investorId,
          token,
        });
        setSessionToken(res.data.session_token);
        if (res.data.first_message) {
          // New session — use the first message directly
          setMessages([{ role: 'assistant', content: res.data.first_message }]);
        } else if (res.data.status === 'IN_PROGRESS') {
          // Resumed session — fetch existing exchanges
          try {
            const historyRes = await axios.get(`${API_URL}/suitability/public/history`, {
              params: { session_token: res.data.session_token },
            });
            if (historyRes.data.exchanges?.length > 0) {
              setMessages(historyRes.data.exchanges.map(ex => ({
                role: ex.role === 'AI' ? 'assistant' : 'user',
                content: ex.content,
              })));
            }
          } catch {
            // If history fetch fails, show a prompt to continue
            setMessages([{ role: 'assistant', content: 'Welcome back. You have an assessment in progress. Please continue where you left off.' }]);
          }
        }
      } catch (err) {
        const errData = err.response?.data;
        const errCode = errData?.error;
        const errMsg  = errData?.message || '';
        if (errCode === 'UNDERAGE_INVESTOR' || errMsg.includes('18 years')) {
          setError('You must be at least 18 years old to invest. Your application has been rejected.');
        } else {
          setError(errMsg || errCode || 'Failed to start suitability assessment.');
        }
      } finally {
        setLoading(false);
      }
    };
    startSession();
 // eslint-disable-next-line react-hooks/exhaustive-deps
}, [warned]);

  // Auto scroll to bottom
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || sending || completed) return;
    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setSending(true);
    try {
      const res = await axios.post(`${API_URL}/suitability/public/respond`, {
        session_token: sessionToken,
        message: userMsg,
      });
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.message }]);
      if (res.data.status === 'UNDERAGE_REJECTED' || res.data.message?.includes('UNDERAGE_INVESTOR')) {
        setError('You must be at least 18 years old to invest. Your application has been rejected and the compliance team has been notified.');
        return;
      }
      if (res.data.status === 'ASSESSMENT_COMPLETE') {
        // Call finalise to get the actual assessment result
        try {
          const finalRes = await axios.post(`${API_URL}/suitability/public/finalise`, {
            session_token: sessionToken,
          });
          setCompleted(true);
          onSessionComplete(finalRes.data);
        } catch {
          // Even if finalise fails, mark as complete
          setCompleted(true);
          onSessionComplete({ suitability_result: 'BORDERLINE', recommended_action: 'MANUAL_REVIEW' });
        }
      }
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: chat.errorGeneric }]);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  if (!warned) return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: C.navy, margin: '0 0 6px' }}>{t.title}</h2>
      <p style={{ fontSize: 13, color: C.textSm, margin: '0 0 24px' }}>{t.sub}</p>
      <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 12, padding: '20px 22px', marginBottom: 16 }}>
        <p style={{ margin: '0 0 12px', fontSize: 13, fontWeight: 700, color: '#92400e' }}>{t.heading}</p>
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: '#92400e', lineHeight: 1.9 }}>
          {t.bullets.map((b, i) => (
            <li key={i} dangerouslySetInnerHTML={{ __html: b }} />
          ))}
        </ul>
      </div>
      <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 10, padding: '14px 18px', marginBottom: 24, fontSize: 13, color: '#0369a1' }}>
        {t.privacy}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <button onClick={onBack} style={btnStyle(C.white, C.navy, C.navy)}>{t.back}</button>
        <button onClick={() => setWarned(true)} style={btnStyle(C.navy, C.white)}>{t.begin}</button>
      </div>
    </div>
  );

  if (loading) return (
    <div style={{ textAlign: 'center', padding: '60px 0' }}>
      <p style={{ fontSize: 28, margin: '0 0 12px' }}>🤖</p>
      <p style={{ fontSize: 14, color: C.textSm }}>{chat.loading}</p>
    </div>
  );

  if (error) {
    const isUnderage = error.includes('18 years') || error.includes('18 ans') || error.includes('18 Jahre');
    return (
      <div style={{ textAlign: 'center', padding: '20px 0' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>
          {isUnderage ? '🔞' : '⚠️'}
        </div>
        <h3 style={{ fontSize: 18, fontWeight: 700, color: isUnderage ? C.red : C.navy, margin: '0 0 12px' }}>
          {chat.errorTitle(isUnderage)}
        </h3>
        <div style={{ background: isUnderage ? '#fee2e2' : '#fff3cd', borderRadius: 10, padding: '16px 20px', marginBottom: 20, textAlign: 'left' }}>
          <p style={{ margin: '0 0 8px', fontSize: 13, color: isUnderage ? '#991b1b' : '#92400e', fontWeight: 600 }}>
            {chat.errorLabel(isUnderage)}
          </p>
          <p style={{ margin: 0, fontSize: 13, color: isUnderage ? '#991b1b' : '#92400e', lineHeight: 1.6 }}>
            {isUnderage ? chat.errorMsg : error}
          </p>
        </div>
        {isUnderage && (
          <div style={{ background: '#f8fafc', borderRadius: 10, padding: '16px 20px', marginBottom: 20, textAlign: 'left', border: `1px solid ${C.border}` }}>
            <p style={{ margin: '0 0 6px', fontSize: 13, fontWeight: 700, color: C.navy }}>{chat.errorNext}</p>
            <p style={{ margin: '0 0 8px', fontSize: 13, color: C.textMd, lineHeight: 1.6 }}>{chat.errorNextBody}</p>
            <p style={{ margin: 0, fontSize: 13, color: C.textMd, lineHeight: 1.6 }}>{chat.errorNextFooter}</p>
          </div>
        )}
        {!isUnderage && (
          <button onClick={onBack} style={btnStyle(C.white, C.navy, C.navy)}>{chat.back}</button>
        )}
      </div>
    );
  }

  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: C.navy, margin: '0 0 4px' }}>{chat.header}</h2>
      <p style={{ fontSize: 13, color: C.textSm, margin: '0 0 16px' }}>{chat.sub}</p>

      {/* Chat window */}
      <div style={{
        height: 380, overflowY: 'auto',
        background: '#f8fafc', borderRadius: 12,
        border: `1px solid ${C.border}`,
        padding: '16px', marginBottom: 12,
        display: 'flex', flexDirection: 'column', gap: 12,
      }}>
        {messages.map((msg, i) => (
          <div key={i} style={{
            display: 'flex',
            justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
          }}>
            {msg.role === 'assistant' && (
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: C.navy, color: C.white, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, flexShrink: 0, marginRight: 8, marginTop: 2 }}>
                🤖
              </div>
            )}
            <div style={{
              maxWidth: '75%', padding: '10px 14px',
              fontSize: 13, lineHeight: 1.6,
              background: msg.role === 'user' ? C.navy : C.white,
              color: msg.role === 'user' ? C.white : C.text,
              border: msg.role === 'assistant' ? `1px solid ${C.border}` : 'none',
              borderRadius: msg.role === 'user' ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
            }}>
              {msg.content}
            </div>
          </div>
        ))}
        {sending && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: '50%', background: C.navy, color: C.white, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12 }}>🤖</div>
            <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '12px 12px 12px 4px', padding: '10px 14px', fontSize: 13, color: C.textSm }}>
              {chat.thinking}
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Completed banner */}
      {completed && (
        <div style={{
          background: '#f0f9ff', borderRadius: 10, padding: '14px 18px', marginBottom: 12,
          border: '1px solid #bae6fd',
        }}>
          <p style={{ margin: 0, fontWeight: 700, fontSize: 14, color: '#0369a1' }}>{chat.doneBanner}</p>
          <p style={{ margin: '3px 0 0', fontSize: 12, color: C.textSm }}>{chat.doneSubtext}</p>
        </div>
      )}

      {/* Input */}
      {!completed && (
        <div style={{ display: 'flex', gap: 8 }}>
          <textarea
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={chat.placeholder}
            rows={2}
            style={{
              flex: 1, padding: '10px 14px', borderRadius: 8,
              border: `1px solid ${C.border}`, fontSize: 13,
              resize: 'none', fontFamily: 'inherit', outline: 'none',
            }}
          />
          <button onClick={sendMessage} disabled={!input.trim() || sending} style={{
            padding: '10px 18px', borderRadius: 8,
            background: !input.trim() || sending ? '#e5e7eb' : C.navy,
            color: !input.trim() || sending ? C.textXs : C.white,
            border: 'none', fontWeight: 700, fontSize: 13, cursor: 'pointer',
            alignSelf: 'flex-end',
          }}>
            {chat.send}
          </button>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12 }}>
        <button onClick={onBack} disabled={sending} style={btnStyle(C.white, C.navy, C.navy)}>{chat.back}</button>
        {completed && (
          <button onClick={onNext} style={btnStyle(C.navy, C.white)}>{chat.next}</button>
        )}
      </div>
    </div>
  );
};

// ─── Step 4 — Review & Submit ──────────────────────────────────────────────────
const Step4 = ({ personalData, suitabilityResult, onSubmit, onBack, submitting, error, lang = 'EN' }) => {
  const t = (T[lang] || T.EN).review;
  const [amount,  setAmount]  = useState('');
  const [confirm, setConfirm] = useState(false);
  const valid = amount && parseFloat(amount) >= 1000 && confirm;
  const fmtAmt = amount ? `€${parseFloat(amount).toLocaleString('en-LU')}` : '…';

  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: C.navy, margin: '0 0 6px' }}>{t.title}</h2>
      <p style={{ fontSize: 13, color: C.textSm, margin: '0 0 24px' }}>{t.sub}</p>

      {/* Personal summary */}
      <div style={{ background: '#f8fafc', borderRadius: 10, padding: '16px 18px', marginBottom: 16, border: `1px solid ${C.border}` }}>
        <p style={{ margin: '0 0 12px', fontSize: 11, fontWeight: 700, color: C.textXs, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{t.personalSection}</p>
        {[
          [t.labels.name,         personalData.full_name],
          [t.labels.email,        personalData.email],
          [t.labels.nationality,  personalData.nationality],
          [t.labels.investorType, personalData.investor_type?.replace(/_/g, ' ')],
        ].map(([label, value]) => (
          <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: `1px solid #f0f0f0` }}>
            <span style={{ fontSize: 12, color: C.textSm }}>{label}</span>
            <span style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{value || '—'}</span>
          </div>
        ))}
      </div>

      {/* Suitability result */}
      {suitabilityResult && (
        <div style={{ background: '#f0f9ff', borderRadius: 10, padding: '14px 18px', marginBottom: 16, border: '1px solid #bae6fd' }}>
          <p style={{ margin: '0 0 2px', fontSize: 11, fontWeight: 700, color: C.textXs, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{t.suitabilitySection}</p>
          <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#0369a1' }}>{t.suitabilityDone}</p>
        </div>
      )}

      {/* Investment amount */}
      <div style={{ marginBottom: 20 }}>
        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.textMd, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          {t.amountLabel} <span style={{ color: C.red }}>*</span>
        </label>
        <div style={{ position: 'relative' }}>
          <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 16, color: C.textSm, fontWeight: 600 }}>€</span>
          <input
            type="number" min="1000" value={amount}
            onChange={e => setAmount(e.target.value)}
            placeholder="10,000"
            style={{ width: '100%', padding: '12px 14px 12px 32px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 16, fontWeight: 600, color: C.text, boxSizing: 'border-box', outline: 'none' }}
          />
        </div>
        {amount && parseFloat(amount) < 1000 && (
          <p style={{ fontSize: 12, color: C.red, margin: '6px 0 0' }}>{t.amountMin}</p>
        )}
      </div>

      {/* Confirm checkbox */}
      <label style={{
        display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer',
        padding: '14px 16px', borderRadius: 8, marginBottom: 20,
        border: `1px solid ${confirm ? C.navy : C.border}`,
        background: confirm ? '#eff6ff' : C.white, transition: 'all 0.2s',
      }}>
        <input type="checkbox" checked={confirm} onChange={e => setConfirm(e.target.checked)}
          style={{ accentColor: C.navy, width: 18, height: 18, marginTop: 1, flexShrink: 0 }} />
        <span style={{ fontSize: 13, color: C.textMd, fontWeight: 500, lineHeight: 1.5 }}>
          {t.checkboxText(fmtAmt)}
        </span>
      </label>

      {error && (
        <div style={{ background: '#fee2e2', borderRadius: 8, padding: '12px 16px', marginBottom: 20, fontSize: 13, color: C.red, fontWeight: 600 }}>
          ✗ {error}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <button onClick={onBack} disabled={submitting} style={btnStyle(C.white, C.navy, C.navy)}>{t.back}</button>
        <button onClick={() => onSubmit(amount)} disabled={!valid || submitting}
          style={btnStyle(valid && !submitting ? C.navy : '#e5e7eb', valid && !submitting ? C.white : C.textXs)}>
          {submitting ? t.submitting : t.submit}
        </button>
      </div>
    </div>
  );
};

// ─── Success screen ────────────────────────────────────────────────────────────
const SuccessScreen = ({ name, lang = 'EN' }) => {
  const t = (T[lang] || T.EN).success;
  return (
    <div style={{ textAlign: 'center', padding: '40px 0' }}>
      <div style={{ width: 80, height: 80, borderRadius: '50%', background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontSize: 36 }}>
        ✅
      </div>
      <h2 style={{ fontSize: 22, fontWeight: 700, color: C.navy, margin: '0 0 10px' }}>{t.title}</h2>
      <p style={{ fontSize: 14, color: C.textSm, maxWidth: 380, margin: '0 auto 8px', lineHeight: 1.6 }}>
        {t.body(name)}
      </p>
      <p style={{ fontSize: 13, color: C.textXs, maxWidth: 360, margin: '0 auto' }}>
        {t.footer}
      </p>
    </div>
  );
};

// ─── Main Onboarding component ─────────────────────────────────────────────────
const Onboarding = () => {
  const [searchParams] = useSearchParams();
  const token          = searchParams.get('token');

  const [step,             setStep]             = useState(1);
  const [investorId,       setInvestorId]       = useState(null);
  const [tokenValid,       setTokenValid]       = useState(null);
  const [tokenError,       setTokenError]       = useState(null);
  const [submitting,       setSubmitting]       = useState(false);
  const [submitError,      setSubmitError]      = useState(null);
  const [done,             setDone]             = useState(false);
  const [suitabilityResult, setSuitabilityResult] = useState(null);

  const [personalData, setPersonalData] = useState({
    full_name: '', email: '', nationality: '', address_country: '',
    date_of_birth: '', phone: '', investor_type: '', preferred_language: 'EN',
  });

  const [amlData, setAmlData] = useState({
    source_of_wealth: '', source_of_wealth_details: '',
    source_of_funds: '', source_of_funds_details: '',
    is_pep: false, pep_details: '',
    sanctions_declaration: false,
  });

  useEffect(() => {
    if (!token) {
      setTokenValid(false);
      setTokenError('No invite token found. Please use the link from your invite email.');
      return;
    }
    axios.get(`${API_URL}/investors/verify-token?token=${token}`)
      .then(res => {
        setTokenValid(true);
        setInvestorId(res.data.investor_id);
        setPersonalData(d => ({
          ...d,
          ...(res.data.full_name     && { full_name:     res.data.full_name }),
          ...(res.data.email         && { email:         res.data.email }),
          ...(res.data.nationality   && { nationality:   res.data.nationality }),
          ...(res.data.investor_type && { investor_type: res.data.investor_type }),
        }));
      })
      .catch(err => {
        setTokenValid(false);
        setTokenError(err.response?.data?.error || 'Invalid or expired invite link.');
      });
  }, [token]);

  const handleSubmit = async (amount) => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      await axios.post(`${API_URL}/investors/${investorId}/onboarding-complete`, {
        token,
        personal:    personalData,
        suitability: {},
        amount:      parseFloat(amount),
      });
      setDone(true);
    } catch (err) {
      setSubmitError(err.response?.data?.error || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (tokenValid === null) return (
    <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center', color: C.textSm, fontSize: 14 }}>
        <p style={{ fontSize: 28, margin: '0 0 12px' }}>⏳</p>
        Verifying your invite link…
      </div>
    </div>
  );

  if (tokenValid === false) return (
    <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: C.white, borderRadius: 14, padding: '40px 36px', maxWidth: 440, textAlign: 'center', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
        <p style={{ fontSize: 40, margin: '0 0 16px' }}>🔗</p>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: C.navy, margin: '0 0 10px' }}>Invalid Invite Link</h2>
        <p style={{ fontSize: 13, color: C.textSm, lineHeight: 1.6 }}>{tokenError}</p>
        <p style={{ fontSize: 12, color: C.textXs, marginTop: 16 }}>Please contact your fund manager for a new invite link.</p>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: C.bg, padding: '40px 16px' }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <div style={{ width: 36, height: 36, borderRadius: 8, background: C.navy, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>🛡️</div>
          <span style={{ fontSize: 20, fontWeight: 800, color: C.navy, letterSpacing: '-0.02em' }}>RegShield</span>
        </div>
        <p style={{ fontSize: 13, color: C.textSm, margin: 0 }}>Secure Investor Onboarding</p>
      </div>

      {/* Card */}
      <div style={{ maxWidth: 640, margin: '0 auto', background: C.white, borderRadius: 16, padding: '36px 40px', boxShadow: '0 4px 24px rgba(0,0,0,0.08)', border: `1px solid #f0f0f0` }}>
        {done ? (
          <SuccessScreen name={personalData.full_name} lang={personalData.preferred_language || 'EN'} />
        ) : (
          <>
            <ProgressBar current={step} />
            {step === 1 && (
              <Step1 data={personalData} setData={setPersonalData} onNext={() => setStep(2)} investorId={investorId} token={token} />
            )}
            {step === 2 && (
              <Step2
                onNext={async () => {
                  try {
                    console.log('AML save — investorId:', investorId, 'amlData:', amlData);
                    await axios.patch(`${API_URL}/investors/${investorId}/aml`, amlData, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                  } catch (err) {
                    console.error('AML save failed:', err.message);
                  }
                  setStep(3);
                }}
                onBack={() => setStep(1)}
                lang={personalData.preferred_language || 'EN'}
                amlData={amlData}
                setAmlData={setAmlData}
              />
            )}
            {step === 3 && (
              <Step3
                investorId={investorId}
                token={token}
                onNext={() => setStep(4)}
                onBack={() => setStep(2)}
                onSessionComplete={result => setSuitabilityResult(result)}
                lang={personalData.preferred_language || 'EN'}
              />
            )}
            {step === 4 && (
              <Step4
                personalData={personalData}
                suitabilityResult={suitabilityResult}
                onSubmit={handleSubmit}
                onBack={() => setStep(3)}
                submitting={submitting}
                error={submitError}
                lang={personalData.preferred_language || 'EN'}
              />
            )}
          </>
        )}
      </div>

      <p style={{ textAlign: 'center', fontSize: 11, color: '#d1d5db', marginTop: 24 }}>
        🔒 Secured by RegShield · GDPR compliant · Data processed in Luxembourg
      </p>
    </div>
  );
};

export default Onboarding;